/* eslint-disable react/require-default-props */
import * as React from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import { IOption } from 'interfaces';
import { styled } from '@mui/material/styles';
import {
  Box, CircularProgress, Grid, InputBase, Tooltip, Typography,
} from '@mui/material';
import './index.scss';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CustomSelect = styled(Select<string[]>)<{ autoHeight: boolean }>(({ theme, autoHeight }) => ({
  opacity: 1,
  backgroundColor: theme.palette.grey.A100,
  border: 0,
  borderRadius: '6px',
  fontSize: '18px',
  fontWeight: 500,
  outline: 'none',
  height: autoHeight ? 'auto' : '48px',
  minHeight: '48px',
  width: '100%',
  color: theme.palette.text.secondary,
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    borderRadius: '6px',
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    color: theme.palette.text.secondary,
  },
  '&.Mui-focused': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  '&.Mui-focused .MuiOutlinedInput-input': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey.A400,
    borderRadius: '6px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
  },
  paddingTop: autoHeight ? '6px' : '0',
  paddingBottom: autoHeight ? '6px' : '0',
}));

const BootstrapInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    position: 'relative',
    border: 'none',
    padding: '0 0.8rem',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
  },
}));

interface PrimaryMultiSelectProps {
  value: string[];
  onChange: any;
  name?: string;
  label?: string;
  innerLabel?: string;
  options: IOption[];
  placeholder?: string;
  loading?: boolean;
  input?: React.ReactElement<any, any>;
  renderValue?: ((value: string[]) => React.ReactNode) | undefined;
  autoHeight?: boolean;
  trimOptions?: number;
}
const PrimaryMultiSelect = React.forwardRef(({
  options, name, label, innerLabel, placeholder, loading, value, onChange, input, renderValue,
  autoHeight = false, trimOptions,
}: PrimaryMultiSelectProps, ref) => {
  const getValue = (selectedId: string[]) => options.filter((item) => (
    selectedId.includes(item.id as string)
  )).map((item) => (item.text)).join(', ');

  return (
    <Box>
      <div>
        {label && <Typography variant="body1">{label}</Typography>}
        <Grid
          container
          className="primary-multi-select-wrapper"
        >
          {loading ? <div style={{ margin: 'auto' }}><CircularProgress size={20} /></div> : (
            <>
              {innerLabel && (
                <Grid>
                  <InputLabel>
                    <Typography variant="body1">{innerLabel}</Typography>
                  </InputLabel>
                </Grid>
              )}

              <Grid sx={{ flex: 2 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    ref={ref}
                    multiple
                    displayEmpty
                    name={name}
                    value={value}
                    onChange={onChange}
                    renderValue={renderValue === undefined
                      ? ((selected) => placeholder as string) : renderValue}
                    MenuProps={MenuProps}
                    input={input}
                    autoHeight
                  >
                    <MenuItem disabled value="">
                      <div style={{ color: '#B2B2B2' }}>{placeholder}</div>
                    </MenuItem>
                    {options.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        <Checkbox checked={value.indexOf(option.value as string) > -1} />
                        {trimOptions && option.text.toString().length > trimOptions ? (
                          <Tooltip title={option.text}>
                            <span>
                              {`${option.text.toString().substring(0, trimOptions)}...`}
                            </span>
                          </Tooltip>
                        ) : (
                          option.text
                        )}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>

      </div>
    </Box>
  );
});

PrimaryMultiSelect.defaultProps = {
  input: <BootstrapInput />,
  renderValue: undefined,
};

export default PrimaryMultiSelect;
